import type { getLayerTransform } from '@/lib/layerTransform';
import * as THREE from 'three';
import { alignmentKey, buildAlignment, concatMultiLine, drapePointsCounted, reversePoints, type Alignment, type AlignmentZSource, type DrapeSampler, type KeyPoint } from './alignment';

/** Shape of `feature.properties.alignment`. Presence of the object = "this line is an alignment". */
export interface FeatureAlignmentProps {
  name?: string;
  staStart?: number;
  reverse?: boolean;
  drapeLayerId?: string | null;
}

export function parseFeatureAlignmentProps(properties: unknown): FeatureAlignmentProps | null {
  if (!properties || typeof properties !== 'object') return null;
  const raw = (properties as Record<string, unknown>).alignment;
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const r = raw as Record<string, unknown>;
  const out: FeatureAlignmentProps = {};
  if (typeof r.name === 'string') out.name = r.name;
  if (typeof r.staStart === 'number' && Number.isFinite(r.staStart)) out.staStart = r.staStart;
  if (typeof r.reverse === 'boolean') out.reverse = r.reverse;
  if (typeof r.drapeLayerId === 'string') out.drapeLayerId = r.drapeLayerId;
  return out;
}

export function featureToAlignment({
  featureId,
  layerId,
  serial,
  geometry,
  props,
  transform,
  drape,
}: {
  featureId: string;
  layerId: string;
  serial: number;
  geometry: { type: string; coordinates: unknown };
  props: FeatureAlignmentProps;
  transform: ReturnType<typeof getLayerTransform>;
  drape: DrapeSampler | null;
}): Alignment | null {
  let parts: number[][][];
  if (geometry.type === 'LineString') parts = [geometry.coordinates as number[][]];
  else if (geometry.type === 'MultiLineString') parts = geometry.coordinates as number[][][];
  else return null;

  let points = concatMultiLine(parts);
  if (props.reverse) points = reversePoints(points);

  if (!transform.isIdentity) {
    const v = new THREE.Vector3();
    for (let i = 0; i < points.length; i += 3) {
      v.set(points[i], points[i + 1], points[i + 2]).applyQuaternion(transform.quaternion).add(transform.offsetVec);
      points[i] = v.x;
      points[i + 1] = v.y;
      points[i + 2] = v.z;
    }
  }
  // A drape that landed is the vertex Z's provenance; one that missed the
  // surface entirely leaves both the geometry and the (geometry-derived)
  // zSource alone, so a 2D GeoJSON line still reports `NONE`.
  let zSource: AlignmentZSource | undefined;
  if (drape) {
    const draped = drapePointsCounted(points, drape);
    if (draped.hits > 0) {
      points = draped.points;
      zSource = 'DRAPED';
    }
  }

  // One VERTEX key point per interior vertex (computed on the deduped polyline by buildAlignment's cumLengths).
  const draft = buildAlignment({
    key: alignmentKey(layerId, featureId),
    source: 'FEATURE',
    layerId,
    featureId,
    name: props.name?.trim() || `Feature #${serial}`,
    points,
    staStart: props.staStart ?? 0,
    equations: [],
    zSource,
  });
  if (!draft) return null;
  const keyPoints: KeyPoint[] = [];
  for (let i = 1; i < draft.cumLengths.length - 1; i++) keyPoints.push({ station: draft.cumLengths[i], kind: 'VERTEX' });
  return buildAlignment({ ...draft, keyPoints });
}
